/**
 * Shared scaffolding for the organization settings pages (company profile,
 * team, branding): the active org's profile, the
 * save/dirty state every editable field uses, and the states a page renders
 * when there is no org, no domain API, or no admin rights.
 *
 * @module components/settings/orgSettingsShared
 */
import { useCallback, useEffect, useState, type ReactNode } from "react";

import { useActiveOrg, useOrgs } from "../../state/orgs";
import {
  UNAVAILABLE_MESSAGES,
  useOrgProfile,
  type OrgMemberAction,
  type OrgProfilePatch,
  type OrgProfileSnapshot,
  type OrgProfileView,
} from "../../state/orgProfile";
import { Button } from "../ui/button";
import { SettingsPageContainer, SettingsSection } from "./settingsLayout";

export interface OrgSettingsPageState {
  readonly org: ReturnType<typeof useActiveOrg>;
  readonly profile: OrgProfileSnapshot | null;
  readonly canManage: boolean;
  readonly save: (patch: OrgProfilePatch) => Promise<string | null>;
  readonly applyMemberAction: (userId: string, action: OrgMemberAction) => Promise<string | null>;
  readonly invite: OrgProfileView["invite"];
  readonly revokeInvitation: OrgProfileView["revokeInvitation"];
  readonly setMemberOnDemandUsage: OrgProfileView["setMemberOnDemandUsage"];
  readonly setMemberModelAllowance: OrgProfileView["setMemberModelAllowance"];
  /** Rendered instead of the page when the org or its profile is unavailable. */
  readonly fallback: ReactNode | null;
}

/**
 * Resolve the active org and its profile, returning a ready-to-render
 * fallback for every not-ready state so each page stays about its own
 * settings.
 */
export function useOrgSettingsPage(title: string): OrgSettingsPageState {
  const { orgs, isPending: orgsPending, error } = useOrgs();
  const activeOrg = useActiveOrg();
  const org = activeOrg ?? orgs[0] ?? null;
  const {
    profile,
    unavailableReason,
    isPending,
    canManage,
    save,
    applyMemberAction,
    invite,
    revokeInvitation,
    setMemberOnDemandUsage,
    setMemberModelAllowance,
  } = useOrgProfile(org?.id ?? null);

  const message = (() => {
    if (orgsPending || (org && isPending)) return "Loading…";
    if (error) return error;
    if (!org) {
      return "This account isn’t a member of any organization yet. Ask an administrator to invite you.";
    }
    if (!profile) {
      return UNAVAILABLE_MESSAGES[unavailableReason ?? "error"];
    }
    return null;
  })();

  return {
    org,
    profile,
    canManage,
    save,
    applyMemberAction,
    invite,
    revokeInvitation,
    setMemberOnDemandUsage,
    setMemberModelAllowance,
    fallback:
      message === null ? null : (
        <SettingsPageContainer>
          <SettingsSection title={title}>
            <p
              className={`px-3 py-3 text-sm sm:px-4 ${error ? "text-destructive" : "text-muted-foreground"}`}
            >
              {message}
            </p>
          </SettingsSection>
        </SettingsPageContainer>
      ),
  };
}

export interface FieldSaveState<T> {
  readonly draft: T;
  readonly setDraft: (value: T) => void;
  readonly isDirty: boolean;
  readonly isSaving: boolean;
  readonly error: string | null;
  readonly savedAt: number | null;
  readonly submit: () => void;
  readonly reset: () => void;
}

/**
 * Draft state for one settings field: seeded from the server value, marked
 * dirty on edit, cleared once the save round-trips. `isEqual` keeps
 * structural values (lists, objects) from looking permanently dirty.
 */
export function useFieldSave<T>(
  serverValue: T,
  persist: (value: T) => Promise<string | null>,
  isEqual: (a: T, b: T) => boolean = Object.is,
): FieldSaveState<T> {
  const [draft, setDraft] = useState<T>(serverValue);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  // Adopt server values while the field is untouched, so a refresh (or
  // another admin's change) shows through instead of being masked by a
  // stale draft.
  useEffect(() => {
    setDraft((current) => (isEqual(current, serverValue) ? current : serverValue));
    // Only when the server value itself changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverValue]);

  const submit = useCallback(() => {
    setIsSaving(true);
    setError(null);
    void persist(draft).then((failure) => {
      setIsSaving(false);
      setError(failure);
      if (!failure) setSavedAt(Date.now());
    });
  }, [draft, persist]);

  const reset = useCallback(() => {
    setDraft(serverValue);
    setError(null);
  }, [serverValue]);

  return {
    draft,
    setDraft,
    isDirty: !isEqual(draft, serverValue),
    isSaving,
    error,
    savedAt,
    submit,
    reset,
  };
}

/** Save / Cancel pair plus the field's error, shown only once dirty. */
export function FieldSaveBar<T>({ state, label }: { state: FieldSaveState<T>; label: string }) {
  if (!state.isDirty && !state.error) {
    return null;
  }
  return (
    <div className="flex flex-wrap items-center gap-2 pt-2">
      <Button size="sm" disabled={state.isSaving} onClick={state.submit}>
        {state.isSaving ? "Saving…" : `Save ${label}`}
      </Button>
      <Button size="sm" variant="ghost" disabled={state.isSaving} onClick={state.reset}>
        Cancel
      </Button>
      {state.error ? (
        <p role="alert" className="text-xs text-destructive">
          {state.error}
        </p>
      ) : null}
    </div>
  );
}
